import type {
  BuyerImportColumnPlanDto,
  BuyerSmartImportAnalysisDto,
  BuyerSmartImportReviewStateDto,
  BuyerSmartImportTabSelectionDto,
} from "@/api/buyerSmartImport";
import type { BuyerLeadSource } from "@/api/buyers";
import {
  defaultTransformFor,
  sourceDetailFromSuggestion,
  sourceFromSuggestion,
  transformIsLegalFor,
} from "./smartImportVocabulary";

/**
 * The review state — deriving it from an analysis, changing one thing in it,
 * and reducing it to the two signatures the dry-run and the interests call are
 * cached on.
 *
 * <p>Pure functions, no React. The review screen is the only writer; these are
 * what it writes THROUGH, so that "changing a target re-picks its default
 * transform" is one rule in one place rather than a handler on each control.</p>
 */

/** What the landing screen already knows before the file is even read. */
export interface SmartImportPlacement {
  buyerTeamId: string | null;
  assignToSpecialistUserId: string | null;
  advertisementId: string | null;
}

export const EMPTY_PLACEMENT: SmartImportPlacement = {
  buyerTeamId: null,
  assignToSpecialistUserId: null,
  advertisementId: null,
};

/**
 * The starting review state.
 *
 * <p>🔴 <b>Lead tabs are ticked and nothing else is</b> (D4). `isLeadTab` is
 * the model's judgement that a tab holds leads at all; a summary sheet or a
 * legend arrives false and stays unticked until somebody says otherwise.</p>
 *
 * <p>🔴 <b>Interests start ON</b> — the switch is an opt-OUT. The industries
 * and geographies a listing implies are the whole reason a buyer who enquired
 * about a Phoenix HVAC business is worth more than a name and an email, and a
 * default of off would mean most imports quietly threw that away.</p>
 */
export function reviewFromAnalysis(
  analysis: BuyerSmartImportAnalysisDto,
  placement: SmartImportPlacement = EMPTY_PLACEMENT,
): BuyerSmartImportReviewStateDto {
  return {
    tabs: analysis.plan.tabs.map<BuyerSmartImportTabSelectionDto>((tab) => ({
      tabName: tab.tabName,
      include: tab.isLeadTab,
      headerRowIndex: tab.headerRowIndex,
      source: sourceFromSuggestion(tab.sourceSuggestion),
      sourceDetail: sourceDetailFromSuggestion(tab.sourceSuggestion),
      columns: tab.columns,
    })),
    cutoff: null,
    inferInterests: analysis.interestsAvailable,
    interests: [],
    buyerTeamId: placement.buyerTeamId,
    assignToSpecialistUserId: placement.assignToSpecialistUserId,
    advertisementId: placement.advertisementId,
  };
}

/**
 * Put every tab's Source on one value (D16).
 *
 * <p>🔴 <b>Shared by the two moments an advertisement can set it</b> — a pick
 * made while the review is already on screen, and one made on the landing,
 * before the file has been read at all. The pickers live on the LANDING, so
 * the second is the ordinary order and the first is the exception; a refill
 * that only handled the live case silently kept D16's own promise in exactly
 * the case an operator meets first.</p>
 *
 * <p>An explicit pick beats the reader's own suggestion. A mapped Source
 * COLUMN still beats both, and does so server-side at compose time — which is
 * why nothing here inspects the columns.</p>
 */
export function withSourceOnEveryTab(
  review: BuyerSmartImportReviewStateDto,
  source: BuyerLeadSource,
): BuyerSmartImportReviewStateDto {
  return { ...review, tabs: review.tabs.map((t) => ({ ...t, source })) };
}

/** Replace one tab, leaving every other tab untouched. */
export function withTab(
  review: BuyerSmartImportReviewStateDto,
  tabName: string,
  change: (tab: BuyerSmartImportTabSelectionDto) => BuyerSmartImportTabSelectionDto,
): BuyerSmartImportReviewStateDto {
  return {
    ...review,
    tabs: review.tabs.map((t) => (t.tabName === tabName ? change(t) : t)),
  };
}

/** Replace one column of one tab. */
export function withColumn(
  review: BuyerSmartImportReviewStateDto,
  tabName: string,
  columnIndex: number,
  change: (column: BuyerImportColumnPlanDto) => BuyerImportColumnPlanDto,
): BuyerSmartImportReviewStateDto {
  return withTab(review, tabName, (tab) => ({
    ...tab,
    columns: tab.columns.map((c) => (c.index === columnIndex ? change(c) : c)),
  }));
}

/**
 * Re-target one column.
 *
 * <p>🔴 <b>The transform follows the target unless the one it already carries
 * is still legal.</b> Two targets exist only to be split, so leaving a stale
 * `none` on a column just re-pointed at `FullName` would store a whole name in
 * the first-name field — and the server would fall that column back to Notes
 * rather than apply it, which the operator sees only as data going missing.
 * Re-picking the default is the behaviour the closed vocabulary assumes.</p>
 */
export function retargetColumn(
  review: BuyerSmartImportReviewStateDto,
  tabName: string,
  columnIndex: number,
  target: string,
): BuyerSmartImportReviewStateDto {
  return withColumn(review, tabName, columnIndex, (column) => ({
    ...column,
    target,
    transform: transformIsLegalFor(target, column.transform)
      ? column.transform
      : defaultTransformFor(target),
  }));
}

// ---------------------------------------------------------------------------
// Predicates the review screen branches on
// ---------------------------------------------------------------------------

/** Tabs the operator ticked. Everything downstream reads only these. */
export function includedTabs(
  review: BuyerSmartImportReviewStateDto,
): BuyerSmartImportTabSelectionDto[] {
  return review.tabs.filter((t) => t.include);
}

/**
 * Whether to offer the cutoff at all (D12).
 *
 * <p>An import with no received-date column has nothing to compare a cutoff
 * against, so the control would be a date picker that silently did nothing.</p>
 */
export function cutoffIsAvailable(review: BuyerSmartImportReviewStateDto): boolean {
  return includedTabs(review).some((t) =>
    t.columns.some((c) => c.target === "inquiry.receivedAt"),
  );
}

/** Whether any included tab still maps a listing title or number (D8). */
export function interestsAreAvailable(review: BuyerSmartImportReviewStateDto): boolean {
  return includedTabs(review).some((t) =>
    t.columns.some(
      (c) =>
        c.target === "inquiry.listingTitle" || c.target === "inquiry.listingNumber",
    ),
  );
}

/**
 * `"HVAC, Plumbing"` → `["HVAC", "Plumbing"]`, dropping blanks and duplicates.
 *
 * <p>The one parse for the comma-separated lists the review screen edits — a
 * column's sentinels and a listing's industries. They are the same operator
 * gesture typed into the same shaped control, so two implementations would
 * eventually disagree about whether `"a,,b"` is two values or three.</p>
 *
 * <p>🔴 <b>Geographies are NOT one of them — see {@link parseGeographyList}.</b>
 * A comma inside a place separates a city from its state rather than one place
 * from the next, which is the carve-out `BuyerImportVocabulary.Transforms.CsvList`
 * states by name on the server.</p>
 *
 * <p>Splits on the COMMA only. Not on `/`, which the server's list transform
 * does split on: the built-in sentinel list contains `n/a`, and a parser that
 * broke it into `n` and `a` would silently stop matching the commonest
 * "nothing here" marker in the corpus.</p>
 */
export function parseCommaList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const value = part.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** The separators a place list may use — every one except the comma. */
const GEOGRAPHY_SEPARATORS = /[;/\n\r]+/;

/**
 * `"Phoenix, AZ; Tucson, AZ"` → `["Phoenix, AZ", "Tucson, AZ"]`.
 *
 * <p>🔴 <b>A comma does NOT separate two places.</b> It separates a city from
 * its state, and splitting on it turns one place into two — a city, and a bare
 * state code standing in for the whole state. The server says so at
 * `BuyerImportVocabulary.Transforms.CsvList`: the list transform splits on
 * `;`, `/` and newline always, and on `,` for every target EXCEPT a geography.
 * This is that rule, client-side, for the one control that edits the list by
 * hand.</p>
 *
 * <p>Paired with {@link formatGeographyList} so the box ROUND-TRIPS. The two
 * have to agree: joining with `", "` and splitting on `;` would read one
 * two-place list back as a single place, which is the same loss pointing the
 * other way and just as quiet.</p>
 */
export function parseGeographyList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(GEOGRAPHY_SEPARATORS)) {
    const value = part.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

/** How a place list is shown for editing — the inverse of {@link parseGeographyList}. */
export function formatGeographyList(values: readonly string[]): string {
  return values.join("; ");
}

// ---------------------------------------------------------------------------
// Signatures
// ---------------------------------------------------------------------------

/**
 * A stable string for the WHOLE approved review state — the dry-run's cache
 * key.
 *
 * <p>🔴 <b>Built field by field rather than by `JSON.stringify` of the DTO.</b>
 * The columns come back from the server as parsed JSON, so their key order is
 * the server's insertion order; stringifying them would make the cache key a
 * function of a serializer's behaviour rather than of the operator's choices,
 * and a deploy that reordered two properties would silently miss every cached
 * dry-run. Every field that changes what the server computes is here, and
 * nothing that does not.</p>
 */
export function reviewSignature(review: BuyerSmartImportReviewStateDto): string {
  const tabs = review.tabs
    .map((t) =>
      [
        t.tabName,
        t.include ? "1" : "0",
        String(t.headerRowIndex),
        t.source,
        // In the signature because it is in the review state the server hashes
        // on commit: a detail edited after the last preview has to re-run the
        // dry-run, or the commit is refused against a plan hash nobody saw.
        t.sourceDetail ?? "",
        t.columns
          .map((c) =>
            [
              c.index,
              c.target,
              c.transform,
              c.format ?? "",
              c.sentinels.join("\u001f"),
            ].join("\u001e"),
          )
          .join("\u001d"),
      ].join("\u001c"),
    )
    .join("\u001b");

  const interests = review.interests
    .map((i) => [i.key, i.industries.join("\u001f"), i.geographies.join("\u001f")].join("\u001e"))
    .join("\u001d");

  return [
    tabs,
    review.cutoff ?? "",
    review.inferInterests ? "1" : "0",
    interests,
    review.buyerTeamId ?? "",
    review.assignToSpecialistUserId ?? "",
    review.advertisementId ?? "",
  ].join("\u001a");
}

/**
 * A stable string for ONLY what the interests call reads — which included tab
 * contributes listings, from which columns, off which header row.
 *
 * <p>Deliberately narrower than {@link reviewSignature}: an interests answer
 * costs a Claude call, and re-buying one because somebody adjusted an unrelated
 * column's sentinels is money spent on an identical answer.</p>
 */
export function listingSignature(review: BuyerSmartImportReviewStateDto): string {
  return includedTabs(review)
    .map((t) =>
      [
        t.tabName,
        String(t.headerRowIndex),
        t.columns
          .filter(
            (c) =>
              c.target === "inquiry.listingTitle" ||
              c.target === "inquiry.listingNumber",
          )
          .map((c) => `${c.index}:${c.target}`)
          .join("\u001f"),
      ].join("\u001e"),
    )
    .join("\u001d");
}
